import type {
  CallManagerInterface,
  VideoConferenceInviteParams,
} from './index.types';
import { useNotificationStore } from 'Modules/notification/store';
import { resolveCallState } from 'Modules/softphone/resolver';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { ConnectionManager } from 'Modules/softphone/services/ConnectionManager';
import { EventService } from 'Modules/softphone/services/EventService';
import { SipService } from 'Modules/softphone/services/SipService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createInboundCallSession } from 'Modules/softphone/utils/callFactories';
import { createLogger } from 'Modules/softphone/utils/logger';
import { resolvePersonByExtension } from 'Modules/softphone/utils/sip';
import type { Invitation, SessionState } from 'sip.js';
import { SessionState as SipSessionState } from 'sip.js';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { isPhoneNumber } from 'Utils/phoneUtil';

import { InboundAudioManager } from './audioManagement';
import {
  InboundCallHandler,
  OutboundCallHandler,
  SessionStateHandler,
  SessionCleanupManager,
  HoldHandler,
  ConferenceHandler,
  TransferHandler,
} from './handlers';
import { VideoConferenceHandler } from './handlers/videoConference';

export type { CallManagerInterface } from './index.types';

const logger = createLogger('CallManager');

/**
 * CallManager - Singleton service for call management
 *
 * This manager centralizes all call-related side effects and logic,
 * preventing issues with multiple hook instances creating duplicate effects.
 */
export class CallManager {
  private static instance: CallManager;
  private unsubscribeRingback: (() => void) | null = null;
  private isInitialized = false;

  // Modular components
  private inboundCallHandler: InboundCallHandler;
  private inboundAudioManager: InboundAudioManager;
  private outboundCallHandler: OutboundCallHandler;
  private sessionStateHandler: SessionStateHandler;
  private sessionCleanupManager: SessionCleanupManager;
  private videoConferenceHandler: VideoConferenceHandler;
  private holdHandler: HoldHandler;
  private transferHandler: TransferHandler;
  private conferenceHandler: ConferenceHandler | null = null;

  private constructor() {
    this.inboundCallHandler = new InboundCallHandler();
    this.inboundAudioManager = new InboundAudioManager();
    this.outboundCallHandler = new OutboundCallHandler();
    this.sessionStateHandler = new SessionStateHandler();
    this.sessionCleanupManager = new SessionCleanupManager();
    this.videoConferenceHandler = new VideoConferenceHandler();
    this.holdHandler = new HoldHandler();
    this.transferHandler = new TransferHandler();
  }

  /**
   * Get or lazily create the ConferenceHandler.
   * The handler is only instantiated when merge is first requested,
   * ensuring it is never created when the feature flag is disabled.
   */
  private getConferenceHandler(): ConferenceHandler {
    if (!this.conferenceHandler) {
      this.conferenceHandler = new ConferenceHandler();
      this.conferenceHandler.setCleanupCallback(() =>
        this.cleanupConferenceCompletely()
      );
    }
    return this.conferenceHandler;
  }

  static getInstance(): CallManager {
    if (!CallManager.instance) {
      CallManager.instance = new CallManager();
    }
    return CallManager.instance;
  }

  /**
   * Initialize the manager and set up store subscriptions
   * Should be called once when the softphone module loads
   * @param rootStoreProps - Optional RootStoreProps for contact/person lookups
   */
  initialize(rootStoreProps?: RootStoreProps): void {
    if (this.isInitialized) {
      logger.warn('Already initialized, skipping');
      return;
    }

    logger.info('Initializing CallManager');

    // Inject RootStoreProps into handlers for contact/store lookups
    if (rootStoreProps) {
      this.inboundCallHandler.setRootStoreProps(rootStoreProps);
      this.videoConferenceHandler.setRootStoreProps(rootStoreProps);
    }

    this.unsubscribeRingback = this.setupRingbackSubscription();
    this.setupInboundCallHandler();

    this.isInitialized = true;
    logger.info('CallManager initialized');
  }

  /**
   * Setup SIP invitation handler for inbound calls
   */
  private setupInboundCallHandler(): void {
    const sipService = SipService.getInstance();
    const userAgent = sipService.getUserAgent();

    if (!userAgent) {
      logger.warn('Cannot setup inbound handler: UserAgent not available');
      return;
    }

    userAgent.delegate = {
      onInvite: (invitation: Invitation) => {
        logger.info('Incoming INVITE received', { callId: invitation.id });
        void this.handleIncomingInvite(invitation);
      },
    };

    logger.debug('Inbound call handler setup complete');
  }

  /**
   * Handle incoming SIP INVITE
   */
  private async handleIncomingInvite(invitation: Invitation): Promise<void> {
    await this.inboundCallHandler.handleIncomingInvite(
      invitation,
      () => this.inboundAudioManager.determineAudioType(),
      (audioType) => this.inboundAudioManager.playAudio(audioType)
    );

    // Wire up notification callbacks
    const callId = invitation.id;
    const callInfo = this.inboundCallHandler.getCallInfo(callId);
    if (callInfo?.notificationId) {
      const notificationStore = useNotificationStore.getState();
      const position = 'tr';
      const notifications = notificationStore.notifications.get(position) || [];
      const notification = notifications.find(
        (n) => n.id === callInfo.notificationId
      );

      if (notification?.inboundCallData) {
        notification.inboundCallData.onAccept = () =>
          void this.acceptCall(callId);
        notification.inboundCallData.onDecline = () =>
          void this.declineCall(callId);

        notificationStore.updateNotification(
          position,
          callInfo.notificationId as string,
          notification
        );
      }
    }

    // Setup session listener for termination
    invitation.stateChange.addListener((state) => {
      if (state === SipSessionState.Terminated) {
        this.inboundCallHandler.handleInboundCallTerminated(callId, () =>
          this.inboundAudioManager.updateAudioPlayback()
        );
      }
    });
  }

  /**
   * Auto-hold current call before accepting a new incoming call or making a new outbound call
   * @private
   */
  private async autoHoldCurrentCall(
    currentCallId: string,
    incomingOrOutboundCallId: string
  ): Promise<void> {
    await this.holdHandler.autoHoldCurrentCall({
      currentCallId,
      newCallId: incomingOrOutboundCallId,
      holdCall: (callId) => this.holdCall(callId),
    });
  }

  /**
   * Accept SIP invitation with configured microphone
   * @private
   */
  private async acceptSipInvitation(
    invitation: Invitation,
    selectedMicrophone: string | null
  ): Promise<void> {
    const sipService = SipService.getInstance();
    const acceptOptions = sipService.createAcceptOptions(
      selectedMicrophone ?? undefined
    ) as {
      sessionDescriptionHandlerOptions?: {
        constraints?: MediaStreamConstraints;
      };
      sessionDescriptionHandlerModifiers?: ((
        description: RTCSessionDescriptionInit
      ) => Promise<RTCSessionDescriptionInit>)[];
    };
    await invitation.accept(acceptOptions);
  }

  /**
   * Setup remote audio stream with retry logic
   * @private
   */
  private setupRemoteAudioWithRetry(
    callId: string,
    webrtcService: WebRTCService,
    audioService: AudioService
  ): void {
    const setupRemoteAudio = (attempt = 1, maxAttempts = 5) => {
      setTimeout(() => {
        const remoteStream = webrtcService.getRemoteStream(callId);
        if (remoteStream && remoteStream.getTracks().length > 0) {
          logger.info('Setting remote stream for accepted inbound call', {
            callId,
            trackCount: remoteStream.getTracks().length,
            attempt,
          });
          audioService.setRemoteStream(remoteStream);
        } else if (attempt < maxAttempts) {
          logger.warn(
            `No remote stream available yet (attempt ${attempt}/${maxAttempts}), retrying...`,
            { callId }
          );
          setupRemoteAudio(attempt + 1, maxAttempts);
        } else {
          logger.error('Failed to get remote stream after max attempts', {
            callId,
            maxAttempts,
          });
        }
      }, 100 * attempt);
    };

    setupRemoteAudio();
  }

  /**
   * Setup session state listener for accepted call
   * @private
   */
  private setupAcceptedCallListener(
    callId: string,
    invitation: Invitation,
    webrtcService: WebRTCService,
    audioService: AudioService
  ): void {
    const stateChangeHandler = (state: SessionState) => {
      this.sessionStateHandler.handleSessionStateChange({
        callId,
        state,
        direction: 'inbound',
        webrtcService,
        audioService,
        onScheduleCleanup: (callId) =>
          this.sessionCleanupManager.scheduleCallCleanup(
            callId,
            webrtcService,
            audioService
          ),
        onSwitchToHeldCall: (callId) => this.switchToLongestHeldCall(callId),
      });
    };

    invitation.stateChange.addListener(stateChangeHandler);

    // Update listener tracking
    this.sessionCleanupManager.addSessionListener(callId, () => {
      invitation.stateChange.removeListener(stateChangeHandler);
    });
  }

  /**
   * Create session state change callback for outbound calls
   * @private
   */
  private createOutboundSessionStateCallback(
    webrtcService: WebRTCService,
    audioService: AudioService
  ): (callId: string, state: SessionState) => void {
    return (callId: string, state: SessionState) => {
      this.sessionStateHandler.handleSessionStateChange({
        callId,
        state,
        direction: 'outbound',
        webrtcService,
        audioService,
        onScheduleCleanup: (callId) =>
          this.sessionCleanupManager.scheduleCallCleanup(
            callId,
            webrtcService,
            audioService
          ),
        onSwitchToHeldCall: (callId) => this.switchToLongestHeldCall(callId),
      });
    };
  }

  /**
   * Accept incoming call
   */
  async acceptCall(callId: string): Promise<void> {
    logger.info('Accepting call', { callId });

    const callInfo = this.inboundCallHandler.getCallInfo(callId);
    if (!callInfo) {
      logger.error('Call not found', { callId });
      return;
    }

    // Check if session is already terminated
    if (callInfo.invitation.state === SipSessionState.Terminated) {
      logger.warn('Cannot accept - session already terminated', { callId });

      // Cleanup: remove notification and inbound call
      if (callInfo.notificationId) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.removeNotify('tr', callInfo.notificationId);
      }

      const softphoneStore = useSoftphoneStore.getState();
      softphoneStore.removeIncomingCall(callId);

      this.inboundAudioManager.updateAudioPlayback();
      return;
    }

    // Acquire the microphone on the user action, not on page load.
    const hasMicrophone =
      await ConnectionManager.getInstance().ensureMicrophoneAccess('incoming');
    if (!hasMicrophone) {
      logger.warn('Aborting answer - microphone access denied', { callId });
      return;
    }

    try {
      const softphoneStore = useSoftphoneStore.getState();
      const currentCallId = softphoneStore.currentCallId;

      // Auto-hold current call if exists
      if (currentCallId) {
        await this.autoHoldCurrentCall(currentCallId, callId);
      } else {
        logger.debug('No current call to hold', {
          incomingCallId: callId,
        });
      }

      // Stop all inbound audio
      const audioService = AudioService.getInstance();
      audioService.stopAllInbound();

      // Accept the SIP invitation with selected microphone
      await this.acceptSipInvitation(
        callInfo.invitation,
        softphoneStore.selectedMicrophone
      );

      // Right after the 200 OK it sends, so the row sits where the answer
      // happened rather than after the store and listener bookkeeping below.
      EventService.getInstance().logCallEvent('Call answered', {
        callId,
        direction: 'inbound',
        phoneNumber: callInfo.phoneNumber,
      });

      // Setup WebRTC
      const webrtcService = WebRTCService.getInstance();
      webrtcService.setupPeerConnection(callInfo.invitation, callId);

      // Setup remote audio stream with retry logic
      this.setupRemoteAudioWithRetry(callId, webrtcService, audioService);

      // Create call session and move from incomingCalls to activeCalls
      const callSession = createInboundCallSession(
        callInfo.invitation,
        callInfo.phoneNumber,
        callInfo.displayName,
        callInfo.personId
      );
      callSession.state = 'active';
      callSession.answerTime = new Date().toISOString();

      softphoneStore.removeIncomingCall(callId);
      softphoneStore.addCall(callSession);
      softphoneStore.setCurrentCall(callId);

      // Setup session state listener for the accepted call
      this.setupAcceptedCallListener(
        callId,
        callInfo.invitation,
        webrtcService,
        audioService
      );

      // Dismiss notification
      if (callInfo.notificationId) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.removeNotify('tr', callInfo.notificationId);
      }

      logger.info('Call accepted successfully', { callId });
    } catch (error) {
      EventService.getInstance().logCallEvent('Answer failed', {
        callId,
        direction: 'inbound',
        error: error instanceof Error ? error.message : String(error),
      });

      logger.error('Failed to accept call', {
        error: error instanceof Error ? error.message : String(error),
        callId,
      });
    }
  }

  /**
   * Decline incoming call
   */
  async declineCall(callId: string): Promise<void> {
    logger.info('Declining call', { callId });

    const callInfo = this.inboundCallHandler.getCallInfo(callId);
    if (!callInfo) {
      logger.error('Call not found', { callId });
      return;
    }

    // Check if session is already terminated
    if (callInfo.invitation.state === SipSessionState.Terminated) {
      logger.warn('Cannot decline - session already terminated', { callId });

      // Cleanup: remove notification and inbound call
      if (callInfo.notificationId) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.removeNotify('tr', callInfo.notificationId);
      }

      const softphoneStore = useSoftphoneStore.getState();
      softphoneStore.removeIncomingCall(callId);

      this.inboundAudioManager.updateAudioPlayback();
      return;
    }

    try {
      // Recorded before the reject, so the row sits above the 603 it produces.
      EventService.getInstance().logCallEvent('Call declined', {
        callId,
        direction: 'inbound',
        phoneNumber: callInfo.phoneNumber,
        statusCode: 603,
      });

      // Reject the SIP invitation with 603 Decline status code
      // This ensures the caller immediately gets a busy/decline response
      // instead of continuing to ring until timeout
      await callInfo.invitation.reject({ statusCode: 603 });

      // Dismiss notification
      if (callInfo.notificationId) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.removeNotify('tr', callInfo.notificationId);
      }

      // Remove from incomingCalls store
      const softphoneStore = useSoftphoneStore.getState();
      softphoneStore.removeIncomingCall(callId);

      // Update audio playback
      this.inboundAudioManager.updateAudioPlayback();

      logger.info('Call declined successfully', { callId });
    } catch (error) {
      logger.error('Failed to decline call', {
        error: error instanceof Error ? error.message : String(error),
        callId,
      });
    }
  }

  /**
   * Make an outbound call
   * @param phoneNumber - Phone number to call (for external calls)
   * @param personId - Optional personId for internal calls
   */
  async makeCall(phoneNumber: string, personId?: string): Promise<void> {
    const store = useSoftphoneStore.getState();
    const currentCallId = store.currentCallId;

    logger.info('Making outbound call', { phoneNumber, personId });

    // Acquire the microphone on the user action, not on page load.
    const hasMicrophone =
      await ConnectionManager.getInstance().ensureMicrophoneAccess('outgoing');
    if (!hasMicrophone) {
      logger.warn('Aborting outbound call - microphone access denied', {
        phoneNumber,
      });
      return;
    }

    // Resolve extension to personId via SIP directory when needed
    if (!personId && !isPhoneNumber(phoneNumber)) {
      const resolved = await resolvePersonByExtension(phoneNumber);

      if (resolved) {
        personId = resolved;
        logger.info('Resolved extension to person via SIP directory', {
          extension: phoneNumber,
          personId,
        });
      }
    }

    // Auto-hold current call if exists
    if (currentCallId) {
      await this.autoHoldCurrentCall(currentCallId, phoneNumber);
    } else {
      logger.debug('No current call to hold', {
        newCallTo: phoneNumber,
      });
    }

    const webrtcService = WebRTCService.getInstance();
    const audioService = AudioService.getInstance();

    // Create session state callback
    const sessionStateCallback = this.createOutboundSessionStateCallback(
      webrtcService,
      audioService
    );

    // Delegate to OutboundCallHandler with session state callback
    const removeListener = await this.outboundCallHandler.makeCall(
      phoneNumber,
      personId,
      sessionStateCallback
    );

    // Track listener for cleanup - get the NEW call ID after makeCall
    const newCallId = useSoftphoneStore.getState().currentCallId;
    if (newCallId) {
      this.sessionCleanupManager.addSessionListener(newCallId, removeListener);
    } else {
      logger.warn('No newCallId to track listener', {
        phoneNumber,
        personId,
      });
    }

    logger.info('Outbound call initiated via OutboundCallHandler', {
      phoneNumber,
      personId,
    });
  }

  /**
   * Merge all active/held calls into a conference
   * Delegates to ConferenceHandler for SIP REFER logic.
   * The ConferenceHandler is lazily initialized on first merge request,
   * ensuring it is never created when the feature flag is disabled.
   */
  async mergeCallsToConference(): Promise<void> {
    await this.getConferenceHandler().createConference();
  }

  /**
   * Switch to the longest held call if the terminated call was current
   * This is called after a call ends (either locally or remotely)
   * @param terminatedCallId - ID of the call that was terminated
   * @private
   */
  private switchToLongestHeldCall(terminatedCallId: string): void {
    const store = useSoftphoneStore.getState();

    // Only switch if the terminated call was the current call
    if (terminatedCallId !== store.currentCallId) {
      return;
    }

    // Find all held calls (excluding the terminated one)
    const heldCalls = Array.from(store.activeCalls.values())
      .filter((c) => c.isOnHold && c.id !== terminatedCallId)
      .sort((a, b) => b.duration - a.duration);

    if (heldCalls.length > 0) {
      const longestHeldCall = heldCalls[0];
      store.setCurrentCall(longestHeldCall.id);
    } else {
      store.setCurrentCall(null);
    }

    // Conference cleanup: if the terminated call was part of a conference,
    // check if the conference should be cleared
    this.cleanupConferenceIfNeeded();
  }

  /**
   * Check if the active conference should be cleared.
   * A conference with 0 or 1 participants doesn't make sense and should be cleared.
   * @private
   */
  private cleanupConferenceIfNeeded(): void {
    const store = useSoftphoneStore.getState();
    const conference = store.activeConference;

    if (!conference) {
      return;
    }

    // Check how many participants are still in active calls
    const activeParticipantCount = conference.participantIds.filter((id) =>
      store.activeCalls.has(id)
    ).length;

    if (activeParticipantCount <= 1) {
      logger.info('Conference has <= 1 active participants, clearing', {
        roomGuid: conference.roomGuid,
        activeParticipantCount,
      });
      store.clearConference();
    }
  }

  /**
   * Fully clean up an active conference: clear store state, remove all
   * participant calls, clean up WebRTC/audio resources, and reset to idle.
   *
   * This is the single source of truth for conference teardown, called from:
   * - The roomSession stateChange listener (via ConferenceHandler callback)
   * - hangUp() when the room session is already terminated or BYE fails
   *
   * The method is idempotent — safe to call multiple times.
   */
  cleanupConferenceCompletely(): void {
    const store = useSoftphoneStore.getState();
    const conference = store.activeConference;

    if (!conference) {
      logger.debug('cleanupConferenceCompletely: no active conference');
      return;
    }

    const participantIds = [...conference.participantIds];

    logger.info('Cleaning up conference completely', {
      roomGuid: conference.roomGuid,
      participantCount: participantIds.length,
    });

    // Clear conference state (also resets conferenceId on calls)
    store.clearConference();

    // Clean up WebRTC peer connections, session listeners, and remove calls
    const webrtcService = WebRTCService.getInstance();
    for (const id of participantIds) {
      webrtcService.cleanup(id);
      this.sessionCleanupManager.cleanupSessionListener(id);
      store.removeCall(id);
    }

    // Clean up audio
    AudioService.getInstance().setRemoteStream(null);

    // Fallback to a held 1:1 call if one exists (e.g. user had a call on hold
    // while in the conference). Otherwise reset to idle.
    const remainingHeldCall = Array.from(store.activeCalls.values())
      .filter((c) => c.isOnHold && !participantIds.includes(c.id))
      .sort((a, b) => b.duration - a.duration)[0];

    if (remainingHeldCall) {
      logger.info('Conference ended, falling back to held call', {
        callId: remainingHeldCall.id,
      });
      store.setCurrentCall(remainingHeldCall.id);
    } else {
      store.setCurrentCall(null);
    }

    logger.info('Conference cleanup complete');
  }

  /**
   * End an active call.
   *
   * When an active conference exists, the hang-up targets the conference room
   * session instead of the individual call (whose SIP session may already be
   * terminated after REFER/BYE). The roomSession stateChange listener
   * (registered in ConferenceHandler.registerRoomSessionListener) handles
   * full conference cleanup automatically.
   *
   * @param callId - ID of the call to end
   */
  async hangUp(callId: string): Promise<void> {
    const webrtcService = WebRTCService.getInstance();
    const audioService = AudioService.getInstance();
    const store = useSoftphoneStore.getState();

    logger.info(`Hanging up call: ${callId}`);

    // --- Conference-aware hang-up ---
    // During an active conference the individual call sessions are already
    // terminated (after REFER/BYE). Sending BYE to the room session is the
    // correct way to end the conference for all participants.
    // However, if the call being hung up is NOT part of the conference (e.g. a
    // separate 1:1 call accepted while the conference is running), we should
    // terminate only that call, not the entire conference.
    const conference = store.activeConference;
    const call = store.activeCalls.get(callId);
    const isCallInConference = call?.conferenceId && conference;

    if (conference && isCallInConference) {
      ConnectionManager.getInstance().clearConnectionLossFeedback();

      logger.info(
        'Active conference detected, terminating conference room session',
        {
          roomGuid: conference.roomGuid,
          callId,
        }
      );

      // Stop all call audio before tearing down
      audioService.stopAllCallAudio();

      const roomSession = conference.roomSession;

      if (roomSession && roomSession.state !== SipSessionState.Terminated) {
        try {
          logger.debug('Sending BYE to conference room session');
          await roomSession.bye();
          // The stateChange listener (registerRoomSessionListener) will handle
          // clearing the conference, removing participant calls, and cleaning audio.
        } catch (error) {
          logger.error('Error sending BYE to conference room session', {
            error: (error as Error).message,
          });
          // Fallback: clean up conference directly
          this.cleanupConferenceCompletely();
        }
      } else {
        // Room session already terminated or not available — clean up directly
        logger.debug(
          'Room session already terminated or unavailable, cleaning up conference directly'
        );
        this.cleanupConferenceCompletely();
      }

      return;
    }

    if (!call || !call.session) {
      logger.error('Call not found or has no session');
      return;
    }

    if (store.activeCalls.size === 1) {
      ConnectionManager.getInstance().clearConnectionLossFeedback();
    }

    // Stop all call audio
    audioService.stopAllCallAudio();

    // Check if session is already terminated
    if (call.session.state === 'Terminated') {
      logger.debug('Session already terminated, cleaning up');
      store.updateCall(callId, {
        state: resolveCallState({
          sessionState: SipSessionState.Terminated,
          direction: call.direction,
        }),
        endTime: new Date().toISOString(),
      });
      audioService.setRemoteStream(null);
      this.sessionCleanupManager.scheduleCallCleanup(
        callId,
        webrtcService,
        audioService
      );
      return;
    }

    // No events emitted along this path: the BYE, CANCEL or rejection response
    // it produces is traced off the socket by SipService, and a hand-written
    // copy showed the same message twice — once as the app's intention, once as
    // the message itself.
    try {
      if (call.session.state === 'Established') {
        logger.debug('Sending BYE');
        await call.session.bye();
      } else if (
        call.session.state === 'Establishing' ||
        call.state === 'connecting'
      ) {
        if (call.direction === 'outbound' && 'cancel' in call.session) {
          logger.debug('Canceling outbound call');
          await call.session.cancel();
        } else if ('reject' in call.session) {
          await call.session.reject();
        } else if ('cancel' in call.session) {
          await call.session.cancel();
        }
      } else {
        if (
          'dispose' in call.session &&
          typeof call.session.dispose === 'function'
        ) {
          await call.session.dispose();
        }
      }
    } catch (error) {
      logger.error('Error ending call', { error: (error as Error).message });
    }

    store.updateCall(callId, {
      state: resolveCallState({
        sessionState: SipSessionState.Terminated,
        direction: call.direction,
      }),
      endTime: new Date().toISOString(),
    });

    // Disconnect remote audio
    audioService.setRemoteStream(null);

    this.sessionCleanupManager.scheduleCallCleanup(
      callId,
      webrtcService,
      audioService
    );

    // Switch to longest held call if needed
    this.switchToLongestHeldCall(callId);
  }

  /**
   * Put a specific call on hold
   * @param callId - ID of the call to hold
   */
  async holdCall(callId: string): Promise<void> {
    await this.holdHandler.holdCall(callId);
  }

  /**
   * Resume a call that is on hold
   * @param callId - ID of the call to resume
   * @param options.waitForAnswer - resolve only once the re-INVITE is answered (see ResumeCallParams)
   */
  async resumeCall(
    callId: string,
    options: { waitForAnswer?: boolean } = {}
  ): Promise<void> {
    const webrtcService = WebRTCService.getInstance();
    const audioService = AudioService.getInstance();
    await this.holdHandler.resumeCall({
      callId,
      webrtcService,
      audioService,
      waitForAnswer: options.waitForAnswer,
    });
  }

  /**
   * Toggle hold state for a call (hold if active, resume if on hold)
   * @param callId - ID of the call to toggle
   */
  async toggleHold(callId: string): Promise<void> {
    const webrtcService = WebRTCService.getInstance();
    const audioService = AudioService.getInstance();
    await this.holdHandler.toggleHold({
      callId,
      webrtcService,
      audioService,
    });
  }

  /**
   * Swap the current call with a held call
   * The current call is placed on hold (if not already), and the target call
   * becomes the current call (but remains on hold until user explicitly resumes)
   * @param targetCallId - ID of the held call to swap to
   */
  async swapCall(targetCallId: string): Promise<void> {
    await this.holdHandler.swapCall(targetCallId);
  }

  /**
   * Execute a blind (unattended) transfer for the given call.
   * Delegates to TransferHandler for SIP REFER execution.
   * The call is resumed before the REFER is sent (see resumeBeforeRefer).
   * If the REFER fails, resumes the held call to restore it to active state.
   * If the unhold before the REFER is refused, no REFER is sent and the call
   * stays on hold (no retry: the far end has just refused that re-INVITE).
   *
   * @param callId - ID of the call to transfer
   * @param destination - The transfer destination (phone number or internal route)
   * @throws Error if preconditions are not met, the unhold before the REFER is
   * refused, or the REFER fails
   */
  async blindTransfer(callId: string, destination: string): Promise<void> {
    try {
      await this.resumeBeforeRefer(callId);
    } catch (error) {
      // The unhold itself was refused: no REFER went out, and retrying the same
      // re-INVITE right away would only be refused again.
      logger.error('Could not resume call before REFER, transfer not sent', {
        callId,
        error: (error as Error).message,
      });
      throw error;
    }

    try {
      await this.transferHandler.execute(callId, destination);
    } catch (error) {
      logger.error('Blind transfer failed, attempting to resume call', {
        callId,
        error: (error as Error).message,
      });

      // Resume the held call to restore it to active state (Requirement 7.2)
      try {
        await this.resumeCall(callId);
      } catch (resumeError) {
        logger.error('Failed to resume call after transfer failure', {
          callId,
          error: (resumeError as Error).message,
        });
      }

      throw error;
    }
  }

  /**
   * Complete a warm (attended) transfer by connecting the original caller
   * to the consultation recipient via SIP REFER with Replaces.
   * Delegates to TransferHandler.executeWarmTransfer for SIP execution.
   * The original call is resumed before the REFER is sent (see resumeBeforeRefer).
   *
   * @param originalCallId - ID of the original call (on hold)
   * @param consultationCallId - ID of the consultation call (active)
   * @throws Error if preconditions are not met, the unhold of the original call
   * is refused (no REFER is sent), or the REFER fails
   */
  async completeWarmTransfer(
    originalCallId: string,
    consultationCallId: string
  ): Promise<void> {
    await this.resumeBeforeRefer(originalCallId);
    await this.transferHandler.executeWarmTransfer(
      originalCallId,
      consultationCallId
    );
  }

  /**
   * Take a held call off hold before it is transferred.
   *
   * FreeSWITCH carries the hold into the transfer: with hold_music=indicate_hold
   * it puts the far end on `inactive`, and the leg it originates for the REFER
   * inherits that SDP, so nobody hears anything (SUP-8915). Legacy never held
   * before a blind transfer; resuming first sends the REFER on an active dialog.
   */
  private async resumeBeforeRefer(callId: string): Promise<void> {
    const call = useSoftphoneStore.getState().activeCalls.get(callId);
    if (!call?.isOnHold) {
      return;
    }

    logger.info('Resuming held call before REFER', { callId });
    // Wait for the 2xx: a REFER sent while the unhold is in flight is processed on the held dialog
    await this.resumeCall(callId, { waitForAnswer: true });
  }

  /**
   * Handle incoming video conference invite from Pusher
   * Delegates to VideoConferenceHandler for notification and audio
   */
  async handleVideoConferenceInvite({
    conference,
    personId,
    conversationId,
  }: VideoConferenceInviteParams): Promise<void> {
    await this.videoConferenceHandler.handleIncomingVideoConference(
      conference,
      personId,
      conversationId
    );
  }

  /**
   * Get current state snapshot
   * Used by the hook to expose state
   */
  getState(): CallManagerInterface {
    const store = useSoftphoneStore.getState();

    return {
      active: Array.from(store.activeCalls.values()),
      currentId: store.currentCallId,
      status: store.status,
      hasActiveCalls: store.activeCalls.size > 0,
      activeCallCount: store.activeCalls.size,
      currentCall: store.currentCallId
        ? store.activeCalls.get(store.currentCallId) ?? null
        : null,
      makeCall: (phoneNumber: string, personId?: string) =>
        this.makeCall(phoneNumber, personId),
      hangUp: (callId: string) => this.hangUp(callId),
      acceptCall: (callId: string) => this.acceptCall(callId),
      declineCall: (callId: string) => this.declineCall(callId),
      holdCall: (callId: string) => this.holdCall(callId),
      resumeCall: (callId: string) => this.resumeCall(callId),
      toggleHold: (callId: string) => this.toggleHold(callId),
      swapCall: (targetCallId: string) => this.swapCall(targetCallId),
      mergeCallsToConference: () => this.mergeCallsToConference(),
    };
  }

  /**
   * Subscribe to store for ringback audio management
   * Plays ringback when there's an outbound call in connecting state
   * Stops ringback when call is answered, cancelled, or fails
   */
  private setupRingbackSubscription(): () => void {
    const audioService = AudioService.getInstance();

    return useSoftphoneStore.subscribe(
      (state) => state.activeCalls,
      (activeCalls) => {
        const hasConnectingOutboundCall = Array.from(activeCalls.values()).some(
          (call) => call.direction === 'outbound' && call.state === 'connecting'
        );

        if (hasConnectingOutboundCall) {
          // Only play if not already playing to avoid restarting
          if (!audioService.isPlaying('ringback')) {
            void audioService.play('ringback');
            logger.debug('Playing ringback for outbound call');
          }
        } else {
          // Stop ringback when no connecting outbound calls
          if (audioService.isPlaying('ringback')) {
            void audioService.stop('ringback');
            logger.debug('Stopping ringback - no connecting outbound calls');
          }
        }
      },
      { fireImmediately: false }
    );
  }

  /**
   * Cleanup all resources
   * Should be called when the softphone module unloads
   */
  cleanup(): void {
    logger.info('Cleaning up CallManager');

    // Get active call IDs to preserve their session listeners
    const activeCallIds = new Set(
      useSoftphoneStore.getState().activeCalls.keys()
    );
    const hasActiveCalls = activeCallIds.size > 0;

    if (hasActiveCalls) {
      logger.info('Active calls detected, preserving session listeners', {
        activeCallIds: Array.from(activeCallIds),
      });
    }

    // Stop all inbound audio
    const audioService = AudioService.getInstance();
    audioService.stopAllInbound();

    // Cleanup inbound calls
    this.inboundCallHandler.cleanup();
    // Cleanup video conferences
    this.videoConferenceHandler.cleanup();

    // Unsubscribe from store subscriptions
    try {
      if (
        this.unsubscribeRingback !== null &&
        typeof this.unsubscribeRingback === 'function'
      ) {
        this.unsubscribeRingback();
      }
    } catch (e) {
      logger.warn('Error unsubscribing ringback', {
        error: (e as Error).message,
      });
    } finally {
      this.unsubscribeRingback = null;
    }

    // Cleanup session listeners and timeouts
    this.sessionCleanupManager.cleanup(activeCallIds);

    // Reset isInitialized flag
    this.isInitialized = false;

    logger.info('CallManager cleanup complete');
  }

  /**
   * Check if the manager is initialized
   */
  getIsInitialized(): boolean {
    return this.isInitialized;
  }
}
